import { useEffect, useState, useId, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, AreaChart, Area, CartesianGrid, Legend, LabelList,
} from "recharts";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar, { DATE_PRESETS_EOD } from "../layout/TopBar";
import BottomNav from "../BottomNav";
import { useEodSummaryData } from "../../hooks/useEodSummaryData";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { exportPeriodFor, resolveTokenRange } from "../../utils/reportPeriod";
import { useBranch } from "../../context/BranchContext";
import { useAuth } from "../../context/AuthContext";
import { getEodReports, getEodTaxesData, getEodTaxDiscrepancy, getTenderTypes, getPaymentDetails, todayStr } from "../../services/reportsApi";
import { getCurrencySymbol, formatMoneyForExport } from "../../utils/currency";
import { ReportSectionExportButton } from "./components/ReportExportButton";

import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import KpiTooltip from '../KpiTooltip';
import "./reports.css";
import "./EODSummaryReport.scss";
import { T, useIsDesktop, Bone } from "./reportTokens";
import { TipsGratuityDrill, buildTipsGratuityData, TG_CARD_LABEL, TG_DRILL_TITLE } from "./components/TipsGratuity";
import SalesWaterfall, { buildWaterfallData } from "./components/SalesWaterfall";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchSnapshotDetailsRequest, fetchLiveVoidSummaryRequest, fetchLiveRefundSummaryRequest, fetchLiveStaffPerformanceRequest, fetchLiveCompOrderLogRequest } from "../../redux/reports/clickHouse/clickHouseActions";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import SalesByChannelBreakdown from "./SalesByChannelBreakdown";
import { useSalesByChannelBreakdown } from "../../hooks/useSalesByChannelBreakdown";
import { useSwipeBack } from '../../hooks/useSwipeBack';

/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — EOD SUMMARY REPORT
   Single responsive component: shared EODContent for desktop + mobile.
   ═══════════════════════════════════════════════════════════════════════════ */

function EODDesktopShimmer() {
  const kpiLabels = ["Gross Sales", "Net Sales", "Total Refunds", TG_CARD_LABEL];
  return (
    <div className="rpt-page">
      {/* KPI Row */}
      <div className="rpt-flex-row rpt-gap-16">
        {kpiLabels.map((label) => (
          <div key={label} className="rpt-tile eod-shimmer-kpi">
            <div className="rpt-tile__label eod-shimmer-kpi__label">{label}</div>
            <Bone h={32} r={6} mb={6} />
            <Bone h={14} w={90} r={4} />
          </div>
        ))}
      </div>
      {/* Row: Tender Mix + Channel Revenue */}
      <div className="rpt-flex-row rpt-gap-16">
        <div className="rpt-card rpt-card--r16 eod-shimmer-tender">
          <div className="rpt-section-title">Tender Mix</div>
          <Bone h={140} r={8} />
        </div>
        <div className="rpt-card rpt-card--r16 eod-shimmer-channel">
          <div className="rpt-section-title">Channel Revenue</div>
          <Bone h={140} r={8} />
        </div>
      </div>
      {/* Funds Flow */}
      <div className="rpt-card rpt-card--r16">
        <div className="rpt-section-title">Funds Flow</div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="rpt-flex-row rpt-gap-12 eod-shimmer-funds-row">
            <Bone w={80} h={14} r={4} />
            <Bone w={70} h={14} r={4} />
            <Bone w={70} h={14} r={4} />
            <Bone w={70} h={14} r={4} />
          </div>
        ))}
      </div>
    </div>
  );
}

function EODMobileShimmer() {
  const kpiLabels = ["Gross Sales", "Net Sales", "Total Refunds", TG_CARD_LABEL];
  return (
    <div className="eod-mobile-shimmer">
      {/* KPI grid 2×2 */}
      <div className="eod-mobile-shimmer__grid">
        {kpiLabels.map((label) => (
          <div key={label} className="rpt-tile rpt-tile--compact eod-mobile-shimmer__kpi">
            <div className="rpt-card-label-sm eod-mobile-shimmer__kpi-label">{label}</div>
            <Bone h={24} r={5} mb={4} />
            <Bone h={12} w={60} r={4} />
          </div>
        ))}
      </div>
      {/* Tender Mix */}
      <div className="rpt-card rpt-card--r16 eod-mobile-shimmer__chart-card">
        <div className="rpt-section-title">Tender Mix</div>
        <Bone h={100} r={8} />
      </div>
      {/* Channel Revenue */}
      <div className="rpt-card rpt-card--r16 eod-mobile-shimmer__chart-card">
        <div className="rpt-section-title">Channel Revenue</div>
        <Bone h={100} r={8} />
      </div>
    </div>
  );
}

/* -- Inline Table Shimmer (section-level, within a card) -- */
function EodTableShimmer({ cols = 3, rows = 6 }: { cols?: number; rows?: number }) {
  return (
    <div style={{ padding: '4px 0' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rpt-flex-row rpt-gap-12" style={{ padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
          <Bone w={80} h={13} r={4} />
          {Array.from({ length: cols - 1 }).map((_, j) => (
            <Bone key={j} w={null} h={13} r={4} style={{ flex: 1 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

const buildKpiItemsDefault = () => {
  const zero = `${getCurrencySymbol()}0`;
  return [
    { label: "Gross Sales", value: zero, delta: undefined, icon: "trending_up", iconColor: T.green, blurColor: T.greenSoft, tooltip: "Revenue deducting discounts, complimentary, voids & refunds. The amount paid by customers including tax, tips & other fees" },
    { label: "Net Sales", value: zero, delta: undefined, icon: "account_balance_wallet", iconColor: T.accent, blurColor: T.accentSoft, tooltip: "From Gross sales after deducting tax, tips & gratuity. The revenue retained by you after all deductions." },
    { label: "Total Refunds", value: zero, delta: undefined, drillKey: "refunds", icon: "undo", iconColor: T.red, blurColor: T.redSoft },
    { label: TG_CARD_LABEL, value: zero, delta: undefined, drillKey: "tips", icon: "volunteer_activism", iconColor: T.green, blurColor: T.greenSoft, cardBg: T.greenSoft },
  ];
};

const fmt = (n: any) =>
  (n < 0 ? "-" : "") + getCurrencySymbol() + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0 });
const fmt2 = (n: any) => n.toLocaleString("en-US", { minimumFractionDigits: 2 });

/**
 * ZAR-1762 / TG-2 — EOD's Tips & Gratuity data, in the shared drill-in contract.
 *
 * Reads `data.tipsGratuity`, which useEodSummaryData builds from the same three ClickHouse
 * endpoints — and the same response keys — that Snapshot and Sales use. The only difference is
 * `orderSource: 'direct'`, which is what TG-1 defines EOD as:
 *   snapshot-details → tip, gratuity, orderCount, ordersWithTips, ordersWithGratuity
 *   by-channel       → channel + tip / gratuity   (both channel panels)
 *   staff-perfomance → staff_name + tip / gratuity (both staff rankings)
 */
const buildEodTipsGratuityData = (data: any) => {
  const tg = data?.tipsGratuity;
  return buildTipsGratuityData({
    staff: tg?.staff ?? [],
    channels: tg?.channels ?? [],
    netSales: Number(tg?.netSales) || 0,
    tipsTotal: Number(tg?.totalTips) || 0,
    gratuityTotal: Number(tg?.gratuity) || 0,
    totalOrders: Number(tg?.totalOrders) || 0,
    ordersWithTips: Number(tg?.ordersWithTips) || 0,
    ordersWithGratuity: Number(tg?.ordersWithGratuity) || 0,
  });
};

/**
 * Money for the Tips & Gratuity drill-in only.
 *
 * EOD's shared `fmt` sets no `maximumFractionDigits`, so a computed average rendered as
 * "$33.488". Every other surface shows money at exactly 2dp; this matches them without
 * touching `fmt`, which the rest of the EOD screen depends on.
 */
const fmtTgMoney = (n: number) =>
  (n < 0 ? "-" : "") + getCurrencySymbol() +
  Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// SW7 — the shared Sales Waterfall always shows 2 decimals on Snapshot/Sales (fmtCurrency);
// EOD's own `fmt` above rounds to whole dollars for its KPI cards/tables by design, so the
// waterfall gets its own formatter instead of changing `fmt`'s 25 other call sites.
const fmtWaterfall = (n: any) =>
  (n < 0 ? "-" : "") + getCurrencySymbol() + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDateMMDDYYYY = (value: any) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
};
const fmtDateDisplay = (value: any) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${MONTHS_SHORT[Number(iso[2]) - 1]} ${Number(iso[3])}, ${iso[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};
const fmtCsvDateCell = (value: any) => `="${fmtDateMMDDYYYY(value)}"`;

const EMDASH = '\u2014';
/**
 * Strips corrupted multi-byte characters from API text.
 * â€ (\u00e2\u20ac) is the Windows-1252 mis-read of the first two UTF-8
 * bytes (E2 80) of any 3-byte Unicode character — strip that prefix plus
 * whatever byte follows.  Returns null for blank/dash-only strings so
 * callers can substitute a clean fallback.
 */
const sanitizeApiText = (val: any): string | null => {
  if (val == null) return null;
  const cleaned = String(val)
    .replace(/\u00e2\u20ac./g, '')   // â€ + any trailing char → remove
    .replace(/\u00e2\u0080./g, '')   // â\x80 + any trailing char → remove
    .trim();
  if (!cleaned || cleaned === EMDASH || cleaned === '\u2013') return null;
  return cleaned;
};

/* ── Tax Discrepancy interfaces ── */
interface PaymentDetail {
  orderId: string;
  tenderType: string;
  last4digit: string | null;
  cardType: string | null;
}
interface OrderItem {
  orderDate: string;
  orderNo: string;
  customerName: string;
  itemTotal: number;
  collectedTax: number;
  computedTax: number;
  difference: number;
  employeeName: string;
  paymentDetails: PaymentDetail[];
  splitCount: number;
}
interface TaxDiscrepancyResponse {
  content: OrderItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

function Icon({ name, size = 20, color = T.t3, style = {} }: any) {
  return (
    <span
      className="material-symbols-rounded eod-icon"
      style={{ fontSize: size, color, ...style }}
    >
      {name}
    </span>
  );
}

function ReportCard({ title, subtitle, icon, action, children, flex = 1, minHeight, drillable, onTap, style = {} }: any) {
  const isDesktop = useIsDesktop();
  return (
    <div
      onClick={drillable ? onTap : undefined}
      className={`rpt-card rpt-tile eod-report-card ${!isDesktop ? "rpt-tile--compact" : ""} ${drillable ? "rpt-tile--drillable eod-report-card--drillable" : ""}`}
      style={{
        flex: flex || undefined,
        minHeight: minHeight ?? (isDesktop ? 220 : undefined),
        ...style,
      }}
    >
      {title != null && (
        <div className="eod-report-card__header">
          <div>
            <div className="rpt-section-title eod-report-card__title-group">
              {icon && <Icon name={icon} size={isDesktop ? 18 : 14} color={T.accent} />}
              {title}
            </div>
            {subtitle && <div className="rpt-subtitle" style={{ marginTop: 2, fontSize: isDesktop ? 11 : 10 }}>{subtitle}</div>}
          </div>
          {action ? (
            <div onClick={(e) => e.stopPropagation()}>{action}</div>
          ) : drillable ? (
            <svg width="10" height="10" viewBox="0 0 10 10" className="eod-report-card__chevron">
              <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : null}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * KPI delta percentages, always at 2dp.
 *
 * Deltas reach this card from two different places: most are `parseFloat` of a percentage the
 * API already reports, but the Tips & Grat delta is combined on the FE by `combineTgDeltaPct`
 * (ZAR-1762 / TG-1) and arrives as a raw float — it was rendering as
 * `-56.635847530239566%`. Formatting here rather than at either source keeps every card on this
 * screen consistent, and matches how Snapshot and Sales format their stamp-KPI deltas.
 */
const fmtDeltaPct = (d: any): string => {
  const n = Number(d);
  return Number.isFinite(n) ? n.toFixed(2) : String(d);
};

function KPICard({ label, value, delta, deltaLabel, icon, iconColor, blurColor, cardBg, drillable, onTap, sub, tooltip }: any) {
  const isDesktop = useIsDesktop();
  return (
    <div
      onClick={drillable ? onTap : undefined}
      className={`rpt-tile eod-kpi ${!isDesktop ? "rpt-tile--compact" : ""} ${drillable ? "rpt-tile--drillable eod-kpi--drillable" : ""}`}
      style={{
        background: isDesktop && cardBg ? cardBg : undefined,
        borderColor: drillable ? "rgba(210,58,59,0.2)" : undefined,
      }}
    >
      {isDesktop && (blurColor || iconColor) && (
        <div className="eod-kpi__blur" style={{ background: blurColor || T.greenSoft }} />
      )}
      {drillable && (
        <svg width="10" height="10" viewBox="0 0 10 10" className="eod-kpi__chevron">
          <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {isDesktop ? (
        <>
          <div className="eod-kpi__desktop-header">
            <span className="rpt-tile__label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{label}{tooltip && <KpiTooltip text={tooltip} />}</span>
            {icon && <Icon name={icon} size={18} color={iconColor || T.t3} />}
          </div>
          <div className="rpt-tile__value eod-kpi__desktop-value">{value}</div>
          {sub && (
            <div className="rpt-tile__subtext eod-kpi__desktop-sub">{sub}</div>
          )}
          {delta !== undefined && (
            <div title={deltaLabel} className={`rpt-body eod-kpi__desktop-delta ${delta >= 0 ? "eod-kpi__desktop-delta--up" : "eod-kpi__desktop-delta--down"}`}>
              {delta >= 0 ? "↑" : "↓"} {delta >= 0 ? "+" : ""}{fmtDeltaPct(delta)}%
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rpt-card-label-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{label}{tooltip && <KpiTooltip text={tooltip} isMobile />}</div>
          <div className="rpt-tile__value--sm eod-kpi__mobile-value">{value}</div>
          {sub && (
            <div className="rpt-subtitle eod-kpi__mobile-sub">{sub}</div>
          )}
          {delta !== undefined && (
            <div title={deltaLabel} className="rpt-meta eod-kpi__mobile-delta" style={{ color: delta >= 0 ? T.green : T.red }}>
              {delta >= 0 ? "↑" : "↓"} {delta >= 0 ? "+" : ""}{fmtDeltaPct(delta)}%
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RevenueTrendChart({ data }: any) {
  const isDesktop = useIsDesktop();
  const id = useId().replace(/:/g, "");
  const height = isDesktop ? 220 : 120;
  const margin = isDesktop ? { top: 5, right: 10, left: 0, bottom: 0 } : { top: 5, right: 5, left: -15, bottom: 0 };
  const tickFont = isDesktop ? 10 : 8;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={margin}>
        <defs>
          <linearGradient id={`eodGross-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.accent} stopOpacity={isDesktop ? 0.2 : 0.15} />
            <stop offset="100%" stopColor={T.accent} stopOpacity={0.01} />
          </linearGradient>
          <linearGradient id={`eodNet-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.green} stopOpacity={0.15} />
            <stop offset="100%" stopColor={T.green} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        {isDesktop && <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />}
        <XAxis dataKey="date" tick={{ fontSize: tickFont, fill: T.t3 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: tickFont === 10 ? 10 : 7, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`} />
        <Tooltip formatter={(v) => [fmt(v), ""]} labelStyle={{ color: T.t1 }} />
        <Area type="monotone" dataKey="gross" stroke={T.accent} strokeWidth={isDesktop ? 2.5 : 2} fill={`url(#eodGross-${id})`} name="Gross" />
        <Area type="monotone" dataKey="net" stroke={T.green} strokeWidth={isDesktop ? 2 : 1.5} strokeDasharray={isDesktop ? "4 2" : "3 2"} fill={isDesktop ? `url(#eodNet-${id})` : "transparent"} name="Net" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function normalizeTenderPercentages(items: any[]): any[] {
  if (!items.length) return items;
  const total = items.reduce((s: number, d: any) => s + (Number(d.value) || 0), 0);
  if (total === 0) return items;
  // Scale so values sum to exactly 100, using largest remainder method at 2dp
  const SCALE = 100; // 2 decimal places
  const TARGET = 10000; // 100.00 * 100
  const withFloor = items.map((d, i) => {
    const exact = (Number(d.value) / total) * 100 * SCALE;
    return { ...d, _idx: i, _floor: Math.floor(exact), _rem: exact - Math.floor(exact) };
  });
  const allocated = withFloor.reduce((s, d) => s + d._floor, 0);
  const remainder = TARGET - allocated;
  const sorted = [...withFloor].sort((a, b) => b._rem - a._rem);
  for (let i = 0; i < remainder; i++) sorted[i]._floor += 1;
  return sorted
    .sort((a, b) => a._idx - b._idx)
    .map(({ _idx, _floor, _rem, ...rest }) => ({ ...rest, value: _floor / SCALE }));
}

/** Locate a bearer/JWT token inside an arbitrary auth object (mirrors useEodSummaryData's findToken). */
function findAuthToken(obj: any, depth = 0): string | null {
  if (!obj || typeof obj !== 'object' || depth > 3) return null;
  for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
    if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
  }
  for (const [, val] of Object.entries(obj)) {
    if (typeof val === 'string' && (val as string).length > 50) return val as string;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const found = findAuthToken(val, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

const TENDER_EXPORT_COLORS = ['#D23A3B', '#4A90D9', '#D97706', '#16A34A', '#8B5CF6', '#9CA3B8'];

/** Same shape-building as useEodSummaryData's private buildTenderData — needed here to fetch an OTHER outlet's tender data for "All outlets" export. */
function buildTenderDataForExport(paymentList: any[]): any[] {
  const toNum = (v: any): number => (v != null && v !== '' ? Number(v) : 0);
  const list = Array.isArray(paymentList) ? paymentList : [];
  const total = list.reduce((sum, row) => sum + toNum(row.totalSales ?? row.amount ?? row.sales ?? row.total ?? row.value), 0);
  if (total <= 0) return [];
  const byName: Record<string, any> = {};
  list.forEach((row: any, i: number) => {
    const name = String(row.cardName ?? row.tenderTypeName ?? row.paymentMode ?? row.tenderType ?? row.channelName ?? row.name ?? `Tender ${i + 1}`).trim() || 'Other';
    const amt = toNum(row.totalSales ?? row.amount ?? row.sales ?? row.total ?? row.value);
    const orders = toNum(row.totalOrders ?? row.orderCount ?? row.orders ?? row.count ?? 0);
    if (!byName[name]) byName[name] = { name, amount: 0, orders: 0, color: TENDER_EXPORT_COLORS[Object.keys(byName).length % TENDER_EXPORT_COLORS.length] };
    byName[name].amount += amt;
    byName[name].orders += orders;
  });
  return Object.values(byName)
    .map((d: any) => ({
      name: d.name,
      value: total > 0 ? (d.amount / total) * 100 : 0,
      color: d.color,
      totalSales: d.amount,
      orders: d.orders,
    }))
    .filter((d: any) => d.value > 0)
    .sort((a: any, b: any) => b.value - a.value)
    .slice(0, 12);
}

/**
 * Fetch + normalize the tender breakdown for a SPECIFIC outlet (used by the Tender Type Breakdown
 * export's "All outlets" scope, which needs each OTHER outlet's own data — not the current outlet's
 * already-loaded `tenderList`). Mirrors useEodSummaryData's tender pipeline: live getTenderTypes for
 * "today", paginated getPaymentDetails otherwise, then the same buildTenderData + normalizeTenderPercentages transform.
 */
async function fetchTenderListForOutlet(
  outletLocationId: string,
  token: string | null,
  dateRange: { startDate: string; endDate: string },
  isToday: boolean
): Promise<any[]> {
  const res = isToday
    ? await getTenderTypes({ locationId: outletLocationId, token }).catch(() => null)
    : await getPaymentDetails({ locationId: outletLocationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token, page: 0, size: 50 }).catch(() => null);
  const unwrapped = res?.metaDataInfo?.data ?? res?.content ?? res?.data ?? res?.result ?? res?.response ?? res;
  const paymentList = Array.isArray(unwrapped) ? unwrapped : [];
  const tenderData = buildTenderDataForExport(paymentList);
  return normalizeTenderPercentages(tenderData);
}

function TenderMixChart({ data }: any) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 140 : 100;
  const innerR = isDesktop ? 42 : 30;
  const outerR = isDesktop ? 58 : 44;
  const hasData = Array.isArray(data) && data.some((d: any) => Number(d?.value) > 0);
  if (!hasData) return <SectionEmpty kpiName="tender mix" minHeight={isDesktop ? 200 : 140} />;
  return (
    <div className="eod-tender-mix">
      <ResponsiveContainer width={size} height={size}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={innerR} outerRadius={outerR} dataKey="value" strokeWidth={0}>
            {data.map((d: any, i: number) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{ flex: 1 }}>
        {data.map((d: any) => (
          <div key={d.name} className="eod-tender-mix__legend-item">
            <div className="eod-tender-mix__legend-dot" style={{ background: d.color }} />
            <span className="eod-tender-mix__legend-name">{d.name}</span>
            <span className="eod-tender-mix__legend-value">{Number(d.value).toFixed(2)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelRevenueChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length || !label) return null;
  const row = payload[0]?.payload;
  const maghil = Number(row?.maghil ?? 0);
  const thirdParty = Number(row?.thirdParty ?? 0);
  const total = maghil + thirdParty;
  return (
    <div className="eod-channel-tooltip">
      <div className="eod-channel-tooltip__title">{label}</div>
      <div className="eod-channel-tooltip__total">Total: {fmt(total)}</div>
      <div className="eod-channel-tooltip__maghil">Maghil: {fmt(maghil)}</div>
      <div className="eod-channel-tooltip__third-party">Third Party: {fmt(thirdParty)}</div>
    </div>
  );
}

function ChannelRevenueChart({ data }: any) {
  const isDesktop = useIsDesktop();
  const height = isDesktop ? 240 : 150;
  const margin = isDesktop ? { top: 5, right: 10, left: 0, bottom: 28 } : { top: 5, right: 0, left: -20, bottom: 24 };
  const tickFont = isDesktop ? 10 : 7;
  const maxBar = isDesktop ? 24 : 16;
  const radius: any = isDesktop ? [4, 4, 0, 0] : [3, 3, 0, 0];
  const hasData = Array.isArray(data) && data.some((d: any) => Number(d?.maghil ?? 0) > 0 || Number(d?.thirdParty ?? 0) > 0);
  if (!hasData) return <SectionEmpty kpiName="channel revenue" minHeight={height} />;
  const seriesValues = data.flatMap((d: any) => [Number(d?.maghil ?? 0), Number(d?.thirdParty ?? 0)]).filter((v: number) => v > 0);
  const peakValue = seriesValues.length > 0 ? Math.max(...seriesValues) : 0;
  const fmtAvg = (v: number) => v >= 1000 ? `${getCurrencySymbol()}${(v / 1000).toFixed(1)}k` : `${getCurrencySymbol()}${v.toFixed(0)}`;
  let peakIdx = -1;
  let peakSeries: string = '';
  let scan = 0;
  data.forEach((d: any, i: number) => {
    const m = Number(d?.maghil ?? 0);
    const tp = Number(d?.thirdParty ?? 0);
    if (m > scan) { scan = m; peakSeries = 'maghil'; peakIdx = i; }
    if (tp > scan) { scan = tp; peakSeries = 'thirdParty'; peakIdx = i; }
  });
  const peakLabelColor = peakSeries === 'maghil' ? T.accent : T.orange;
  const peakLabelContent = (series: 'maghil' | 'thirdParty') => (props: any) => {
    const { x, y, width, height, index, value } = props;
    if (index !== peakIdx || series !== peakSeries || !Number(value)) return null;
    const bw = Number(width);
    const bh = Number(height);
    const cx = Number(x) + bw / 2;
    const showInside = bw >= 18 && bh >= 22;
    return (
      <g>
        <text x={cx} y={Number(y) - 4} textAnchor="middle" fill={peakLabelColor} fontSize={isDesktop ? 10 : 8} fontWeight={700}>{fmtAvg(Number(value))}</text>
      </g>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ ...margin, top: (margin.top ?? 0) + 14 }}>
        <XAxis
          dataKey="channel"
          interval={0}
          tick={{ fontSize: tickFont, fill: T.t3 }}
          tickMargin={8}
          angle={-35}
          textAnchor="end"
          height={44}
          axisLine={false}
          tickLine={false}
        />
        <YAxis tick={{ fontSize: tickFont, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${getCurrencySymbol()}${(v / 1000).toFixed(1)}k` : `${getCurrencySymbol()}${v.toFixed(0)}`} />
        <Tooltip content={<ChannelRevenueChartTooltip />} />
        <Bar dataKey="maghil" name="Maghil" fill={T.accent} radius={radius} maxBarSize={maxBar}>
          <LabelList dataKey="maghil" content={peakLabelContent('maghil')} />
        </Bar>
        <Bar dataKey="thirdParty" name={isDesktop ? "Third Party" : "3rd Party"} fill={T.orange} radius={radius} maxBarSize={maxBar}>
          <LabelList dataKey="thirdParty" content={peakLabelContent('thirdParty')} />
        </Bar>
        <Legend
          verticalAlign="bottom"
          align="center"
          iconType="square"
          iconSize={10}
          wrapperStyle={{ fontSize: isDesktop ? 12 : 10, color: T.t2 }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ── Payment helpers ── */
function getPaymentModeIconByTenderType(tenderType: string, cardType: string | null): string {
  const key = (cardType ?? tenderType ?? '').toUpperCase().replace(/[_\s-]/g, '');
  if (['VISA', 'VISACARD'].includes(key)) return 'credit_card';
  if (['MASTERCARD', 'MASTER', 'MC'].includes(key)) return 'credit_card';
  if (['APPLEPAY'].includes(key)) return 'phone_iphone';
  if (['GPAY', 'GOOGLEPAY'].includes(key)) return 'payment';
  if (['CASH'].includes(key)) return 'payments';
  if (['AMEX', 'AMERICANEXPRESS', 'AMERICAN'].includes(key)) return 'credit_card';
  if (['CHASE', 'DCVR', 'DISCOVER', 'CAPITALONE'].includes(key)) return 'credit_card';
  if (['DOORDASH', 'UBEREATS', 'GRUBHUB'].includes(key)) return 'delivery_dining';
  if (['CARDSWIPE', 'ONLINEKEY-IN'].includes(key)) return 'contactless';
  if (['OTHERS'].includes(key)) return 'more_horiz';
  return 'credit_card';
}

function formatPayment(p: PaymentDetail): { label: string; icon: string } {
  // UI: show cardType + last4digit when present, else fall back to tenderType
  const cardInfo = [p.cardType, p.last4digit].filter(Boolean).join(' ');
  const label = cardInfo || p.tenderType || '-';
  return { label, icon: getPaymentModeIconByTenderType(p.tenderType, p.cardType) };
}

function PaymentCell({ paymentDetails, splitCount }: { paymentDetails: any; splitCount: number }) {
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);

  if (!paymentDetails || (Array.isArray(paymentDetails) && paymentDetails.length === 0)) {
    return <span style={{ color: T.t3 }}>-</span>;
  }

  const payments: PaymentDetail[] = typeof paymentDetails === 'string'
    ? [{ tenderType: paymentDetails, last4digit: null, cardType: null, orderId: '' }]
    : paymentDetails;

  if (splitCount <= 1) {
    const { label, icon } = formatPayment(payments[0] ?? { tenderType: '-', last4digit: null, cardType: null, orderId: '' });
    return (
      <span title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', overflow: 'hidden' }}>
        <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t2, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 12, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </span>
    );
  }

  const openTooltip = () => {
    if (badgeRef.current) {
      const r = badgeRef.current.getBoundingClientRect();
      setTooltipPos({ top: r.top - 8, left: r.left + r.width / 2 });
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.blue, lineHeight: 1 }}>call_split</span>
      <span
        ref={badgeRef}
        className="eod-tax-disc__split-badge"
        onMouseEnter={openTooltip}
        onMouseLeave={() => setTooltipPos(null)}
        onClick={() => tooltipPos ? setTooltipPos(null) : openTooltip()}
      >
        +{splitCount}
      </span>
      {tooltipPos && (
        <div
          className="eod-tax-disc__split-tooltip"
          style={{ position: 'fixed', top: tooltipPos.top, left: tooltipPos.left, transform: 'translate(-50%, -100%)' }}
        >
          {payments.map((p, i) => {
            const { label, icon } = formatPayment(p);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                <span className="material-symbols-rounded" style={{ fontSize: 13, color: '#fff', lineHeight: 1 }}>{icon}</span>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}



const TAX_DISC_HEADERS = [
  { key: 'orderNo',       label: 'ORDER #',       sortKey: 'orderNo' },
  { key: 'orderDate',     label: 'DATE & TIME',   sortKey: 'orderDate' },
  { key: 'customerName',  label: 'CUSTOMER',      sortKey: 'customerName' },
  { key: 'itemTotal',     label: 'ORDER AMT',     sortKey: 'itemTotal' },
  { key: 'computedTax',   label: 'COMPUTED TAX',  sortKey: 'computedTax' },
  { key: 'collectedTax',  label: 'COLLECTED TAX', sortKey: 'collectedTax' },
  { key: 'paymentDetails',label: 'PAYMENT MODE',  sortKey: 'paymentDetails' },
  { key: 'employeeName',  label: 'STAFF',         sortKey: 'employeeName' },
];

function TaxDiscrepancyDrillContent({ date, onBack }: { date: string; onBack: () => void }) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const exportDateRangeLabel = exportPeriodFor(date ? undefined : 'Today', date ? { startDate: date, endDate: date } : null)?.label ?? 'Today';
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const [apiData, setApiData] = useState<TaxDiscrepancyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortAsc, setSortAsc] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    getEodTaxDiscrepancy({ locationId, startDate: date, endDate: date, searchParam: '', page, size, sortBy, sortAsc, token })
      .then((res: any) => { if (id === fetchIdRef.current) setApiData(res); })
      .catch(() => { if (id === fetchIdRef.current) setApiData(null); })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  }, [locationId, date, page, size, sortBy, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [date, size]);

  const content = apiData?.content ?? [];
  const totalPages = apiData?.totalPages ?? 1;
  const totalElements = apiData?.totalElements ?? 0;

  const handleSort = (key: string) => {
    if (sortBy === key) setSortAsc(prev => !prev);
    else { setSortBy(key); setSortAsc(false); }
    setPage(1);
  };

  const fetchAllForExport = useCallback((overrideLocationId?: string): Promise<any> => {
    return getEodTaxDiscrepancy({
      locationId: overrideLocationId ?? locationId,
      startDate: date,
      endDate: date,
      searchParam: '',
      page: 1,
      size: totalElements || 10000,
      sortBy,
      sortAsc,
      token,
    });
  }, [locationId, date, totalElements, sortBy, sortAsc, token]);

  const fmtPaymentCsv = (paymentDetails: any): string => {
    if (!paymentDetails) return '-';
    const payments: any[] = typeof paymentDetails === 'string'
      ? [{ tenderType: paymentDetails, cardType: null, last4digit: null }]
      : paymentDetails;
    return payments.map((p: any) => {
      // Downloads format:
      //   tenderType + cardInfo both present → "GooglePay - VISA ...1111"
      //   only cardInfo (no tenderType)      → "VISA ...1111"
      //   only tenderType (no cardInfo)      → "GooglePay"
      const tenderType = sanitizeApiText(p.tenderType) ?? '';
      const cardInfo = [p.cardType, p.last4digit].filter(Boolean).join(' ');
      if (tenderType && cardInfo) return `${tenderType} - ${cardInfo}`;
      return tenderType || cardInfo || '-';
    }).join(', ') || '-';
  };

  const buildTaxDiscExportRows = (rows: OrderItem[]) => rows.map(row => [
    `#${row.orderNo}`,
    row.orderDate,
    sanitizeApiText(row.customerName) ?? '-',
    `${getCurrencySymbol()}${fmt2(row.itemTotal)}`,
    `${getCurrencySymbol()}${fmt2(row.computedTax)}`,
    `${getCurrencySymbol()}${fmt2(row.collectedTax)}`,
    fmtPaymentCsv(row.paymentDetails),
    sanitizeApiText(row.employeeName) ?? '-',
  ]);

  const taxDiscExportHeaders = TAX_DISC_HEADERS.map(h => h.label);

  const title = `Tax Discrepancy Audit Log (${fmtDateDisplay(date)})`;

  const exportAction = (
    <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
      title={title}
      filename={title.replace(/\s+/g, '_').replace(/[/\\?%*:|"<>]/g, '')}
      buildExport={async (_format, opts) => {
        const res = await fetchAllForExport(opts?.locationId);
        return { headers: taxDiscExportHeaders, rows: buildTaxDiscExportRows(res?.content ?? []) };
      }}
    />
  );

  const renderCell = (key: string, row: OrderItem) => {
    if (key === 'orderNo') return <span>#{row.orderNo}</span>;
    if (key === 'orderDate') return <span>{row.orderDate}</span>;
    if (key === 'customerName') return <span>{sanitizeApiText(row.customerName) ?? '-'}</span>;
    if (key === 'itemTotal') return <span>{getCurrencySymbol()}{fmt2(row.itemTotal)}</span>;
    if (key === 'computedTax') return <span>{getCurrencySymbol()}{fmt2(row.computedTax)}</span>;
    if (key === 'collectedTax') return <span>{getCurrencySymbol()}{fmt2(row.collectedTax)}</span>;
    if (key === 'paymentDetails') return <PaymentCell paymentDetails={row.paymentDetails} splitCount={row.splitCount} />;
    if (key === 'employeeName') return <span>{sanitizeApiText(row.employeeName) ?? '-'}</span>;
    return null;
  };

  if (isDesktop) {
    return (
      <div className="eod-tax-disc-drill">
        <button type="button" onClick={onBack} className="eod-tax-disc__back-btn">
          <span className="material-symbols-rounded" style={{ fontSize: 18, lineHeight: 1 }}>arrow_back</span>
          Back to EOD Summary
        </button>
        <ReportCard title={title} icon="gavel" flex={1} minHeight={0} action={exportAction}>
          <>
            {loading ? (
              <EodTableShimmer cols={8} rows={6} />
            ) : content.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: T.t3 }}>No discrepancies found for this date.</div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="eod-table eod-summary-table">
                    <thead>
                      <tr className="eod-table__row">
                        {TAX_DISC_HEADERS.map((h, i) => (
                          <th
                            key={h.key}
                            className={`eod-summary-table__th ${i === 0 ? 'eod-summary-table__th--date' : 'eod-summary-table__th--num'}`}
                            onClick={() => handleSort(h.sortKey)}
                            style={{ cursor: 'pointer', whiteSpace: 'nowrap', ...(h.key === 'paymentDetails' && { maxWidth: 180, width: 180 }), ...(h.key === 'orderDate' && { textAlign: 'left' }) }}
                          >
                            <span className="eod-summary-table__th-inner">
                              {h.label}
                              {sortBy === h.sortKey && <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {content.map((row, idx) => (
                        <tr key={row.orderNo + '-' + idx} className="eod-table__row">
                          {TAX_DISC_HEADERS.map((h, i) => (
                            <td
                              key={h.key}
                              className={`eod-summary-table__td ${i === 0 ? 'eod-summary-table__td--date' : 'eod-summary-table__td--num'}`}
                              style={h.key === 'paymentDetails' ? { maxWidth: 180 } : h.key === 'orderDate' ? { textAlign: 'left' } : undefined}
                            >
                              {renderCell(h.key, row)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', padding: '10px 0 0', fontFamily: T.font }}>
                  <div className="eod-summary-footer__size">
                    <span>Rows per page:</span>
                    <select value={size} onChange={e => { setSize(Number(e.target.value)); setPage(1); }} className="eod-summary-footer__select">
                      {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <span style={{ textAlign: 'center', fontSize: 12, color: T.t3 }}>
                    Showing {Math.min((page - 1) * size + 1, totalElements)}–{Math.min(page * size, totalElements)} of {totalElements}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    {(() => {
                      const btnSt = (disabled: boolean): React.CSSProperties => ({
                        fontSize: 12, fontWeight: 500, fontFamily: T.font,
                        color: disabled ? T.t3 : T.t1,
                        background: T.surface, border: `1px solid ${T.cardBorder}`,
                        borderRadius: 6, padding: '6px 12px',
                        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
                      });
                      const pageBtnSt = (active: boolean): React.CSSProperties => ({
                        fontSize: 12, fontWeight: active ? 600 : 400, fontFamily: T.font,
                        color: active ? '#fff' : T.t1,
                        background: active ? T.accent : T.surface,
                        border: active ? `1px solid ${T.accent}` : `1px solid ${T.cardBorder}`,
                        borderRadius: 6, padding: '6px 10px', cursor: 'pointer', minWidth: 32, textAlign: 'center' as const,
                      });
                      const getPages = (): (number | '...')[] => {
                        const pages: (number | '...')[] = [];
                        if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
                        else {
                          pages.push(1);
                          if (page > 3) pages.push('...');
                          for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
                          if (page < totalPages - 2) pages.push('...');
                          pages.push(totalPages);
                        }
                        return pages;
                      };
                      return (
                        <>
                          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={btnSt(page <= 1)}>Prev</button>
                          {getPages().map((p, i) => p === '...'
                            ? <span key={`e${i}`} style={{ padding: '0 4px', color: T.t3 }}>...</span>
                            : <button key={p} onClick={() => setPage(p as number)} style={pageBtnSt(p === page)}>{p}</button>
                          )}
                          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnSt(page >= totalPages)}>Next</button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </>
        </ReportCard>
      </div>
    );
  }

  // Mobile
  const TAX_DISC_COL_WIDTHS: Record<string, number> = {
    orderNo: 80, orderDate: 120, customerName: 90, itemTotal: 80,
    computedTax: 90, collectedTax: 90, paymentDetails: 100, employeeName: 80,
  };

  return (
    <div className="eod-tax-disc-drill eod-tax-disc-drill--mobile">
      <ReportCard title={title} icon="gavel" flex={1} minHeight={0} action={exportAction}>
        <>
          {loading ? (
            <EodTableShimmer cols={8} rows={5} />
          ) : content.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: T.t3 }}>No discrepancies found for this date.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto', margin: '0 -14px' }}>
                <table className="eod-table eod-tax-disc-mobile-table" style={{ minWidth: 730, width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {TAX_DISC_HEADERS.map(h => (
                        <th
                          key={h.key}
                          onClick={() => handleSort(h.sortKey)}
                          style={{
                            width: TAX_DISC_COL_WIDTHS[h.key],
                            padding: '8px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: T.t3,
                            textAlign: h.key === 'orderNo' ? 'left' : 'right',
                            whiteSpace: 'nowrap',
                            borderBottom: `1px solid ${T.divider}`,
                            cursor: 'pointer',
                            letterSpacing: '0.4px',
                            background: T.surface,
                          }}
                        >
                          {h.label}
                          {sortBy === h.sortKey && <span className="eod-table__sort-icon" style={{ marginLeft: 3 }}>{sortAsc ? '↑' : '↓'}</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {content.map((row, idx) => (
                      <tr key={row.orderNo + '-' + idx} style={{ borderBottom: `1px solid ${T.divider}` }}>
                        {TAX_DISC_HEADERS.map(h => (
                          <td
                            key={h.key}
                            style={{
                              padding: '9px 10px',
                              fontSize: 12,
                              color: T.t1,
                              textAlign: h.key === 'orderNo' ? 'left' : 'right',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {renderCell(h.key, row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 2px 2px' }}>
                <span style={{ fontSize: 11, color: T.t3 }}>
                  Showing {Math.min((page - 1) * size + 1, totalElements)}–{Math.min(page * size, totalElements)} of {totalElements}
                </span>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className="eod-pagination__btn"
                    >Previous</button>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className="eod-pagination__btn"
                    >Next</button>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      </ReportCard>
    </div>
  );
}

// ZAR-1462: legacy WaterfallCard removed — replaced by the shared <SalesWaterfall />.

function ThirdPartyReconciliationCard({ data }: any) {
  const isDesktop = useIsDesktop();
  if (!Array.isArray(data) || data.length === 0) return <SectionEmpty kpiName="third-party reconciliation" minHeight={isDesktop ? 180 : 140} />;
  return (
    <>
      {data.map((p: any) => {
        const variance = p.gross - p.orderTotal;
        const isOk = Math.abs(variance) < 50;
        return (
          <div key={p.platform} className="eod-reconciliation__item">
            <div className="eod-reconciliation__header">
              <div className="eod-reconciliation__platform">
                <Icon name={p.icon} size={isDesktop ? 16 : 14} color={T.accent} />
                <span className="eod-reconciliation__platform-name">{p.platform}</span>
              </div>
              <span className={`eod-reconciliation__variance ${isOk ? "eod-reconciliation__variance--ok" : "eod-reconciliation__variance--warn"}`}>
                {variance >= 0 ? "+" : ""}{fmt(variance)}
              </span>
            </div>
            <div className="eod-reconciliation__details">
              <span>Gross: {fmt(p.gross)}</span>
              <span>Order Total: {fmt(p.orderTotal)}</span>
            </div>
          </div>
        );
      })}
    </>
  );
}

/* ─── Summary Report Table (backend-paginated, sortable, exportable) ─── */

/**
 * Token → date range for every EOD fetch.
 *
 * **Defers to the shared `resolveTokenRange`** (utils/reportPeriod, ZAR-1639) rather
 * than resolving tokens itself. This local copy predates the unified date picker and
 * only knew EOD's own presets — it had no `today` case at all, because
 * `DATE_PRESETS_EOD` omits the Today pill. Once the picker made the selected range
 * persist across reports, arriving on EOD from Sales with "Today" fell through to the
 * last-month fallback below: the control read "Today" while every EOD request asked
 * for the previous calendar month. The shared resolver also anchors on the store's
 * timezone and the 5 AM cutoff, which this copy never did.
 *
 * The fallback is kept only for a token the shared resolver cannot parse, which the
 * presets and the calendar should never produce.
 */
function parseDateRange(datePeriod: string, timeZoneCd?: string) {
  const shared = timeZoneCd ? resolveTokenRange(datePeriod, timeZoneCd) : resolveTokenRange(datePeriod);
  if (shared) return shared;

  const today = todayStr(timeZoneCd);
  const raw = typeof datePeriod === 'string' ? datePeriod.trim() : '';
  const normalized = raw.toLowerCase().replace(/\s+/g, '');
  if (raw.startsWith('custom:')) {
    const p = raw.split(':');
    if (p.length === 3) return { startDate: p[1], endDate: p[2] };
  }
  const d = new Date(`${today}T12:00:00`);
  if (normalized === 'yesterday') {
    const yd = new Date(d);
    yd.setDate(yd.getDate() - 1);
    const y = yd.getFullYear();
    const m = String(yd.getMonth() + 1).padStart(2, '0');
    const day = String(yd.getDate()).padStart(2, '0');
    const yStr = `${y}-${m}-${day}`;
    return { startDate: yStr, endDate: yStr };
  }
  if (normalized === 'thismonth') {
    const start = `${today.slice(0, 7)}-01`;
    return { startDate: start, endDate: today };
  }
  if (normalized === 'lastmonth') {
    const y = d.getFullYear(), m = d.getMonth(); // 0-indexed
    const prevMonth = m === 0 ? 11 : m - 1;
    const prevYear = m === 0 ? y - 1 : y;
    const lastDay = new Date(prevYear, prevMonth + 1, 0).getDate();
    return { startDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-01`, endDate: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}` };
  }
  if (normalized === 'last3months' || normalized === 'last6months') {
    const n = normalized === 'last3months' ? 3 : 6;
    const y = d.getFullYear(), m = d.getMonth(); // 0-indexed current month
    // End = last day of the month immediately preceding the current month
    const endYear = m === 0 ? y - 1 : y;
    const endMonth1 = m === 0 ? 12 : m; // 1-indexed
    const endDay = new Date(endYear, endMonth1, 0).getDate();
    // Start = first day of the month that is N months before the end month
    const startIdx = m - n; // 0-indexed, may be negative
    const startYear = y + Math.floor(startIdx / 12);
    const startMonth1 = ((startIdx % 12) + 12) % 12 + 1; // 1-indexed
    return {
      startDate: `${startYear}-${String(startMonth1).padStart(2, '0')}-01`,
      endDate: `${endYear}-${String(endMonth1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`,
    };
  }
  if (normalized === 'thisyear') {
    return { startDate: `${d.getFullYear()}-01-01`, endDate: today };
  }
  if (normalized === 'lastyear') {
    const y = d.getFullYear() - 1;
    return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
  }
  // Fallback: last month
  const fm = d.getMonth() === 0 ? 11 : d.getMonth() - 1;
  const fy = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
  const fl = new Date(fy, fm + 1, 0).getDate();
  return { startDate: `${fy}-${String(fm + 1).padStart(2, '0')}-01`, endDate: `${fy}-${String(fm + 1).padStart(2, '0')}-${String(fl).padStart(2, '0')}` };
}

// Human-readable active date range for the "All outlets" export file header —
// preset names (e.g. "Today", "This Month") pass through as-is; "custom:start:end" formats nicely.
// The export header's date range comes from `exportPeriodFor` in utils/reportPeriod
// (DPFIX-07). The local copy that lived here returned a label only, never the year, and
// was one of seven that had already drifted apart — see DPFIX-05, where Snapshot's copy
// still tested the deleted 'Custom' token and leaked the raw one into a file.

const SUMMARY_DIRECT_HEADERS = [
  { key: 'date', label: 'DATE', sortKey: 'date' },
  { key: 'maghilTotalSale', label: 'TOTAL SALES', sortKey: 'maghilTotalSale' },
  { key: 'cashSale', label: 'CASH SALES', sortKey: 'cashSale' },
  { key: 'otherSale', label: 'ALTERNATIVE PAYMENTS', sortKey: 'otherSale' },
  { key: 'cardSale', label: 'CREDIT SALES (EXPECTED FUNDS)', sortKey: 'cardSale' },
];

const SUMMARY_THIRDPARTY_HEADERS = [
  { key: 'date', label: 'DATE', sortKey: 'date' },
  { key: 'thirdPartyTotalSale', label: 'TOTAL SALES', sortKey: 'thirdPartyTotalSale' },
  { key: 'ubereats', label: 'UBER EATS', sortKey: 'ubereats' },
  { key: 'grubhub', label: 'GRUBHUB', sortKey: 'grubhub' },
  { key: 'doordash', label: 'DOORDASH', sortKey: 'doordash' },
];

/* ─── Shared pagination footer — matches Tax Discrepancy Audit Log style ─── */
function EodTableFooter({
  page, size, totalElements, totalPages, isDesktop, onPageChange, onSizeChange,
}: {
  page: number; size: number; totalElements: number; totalPages: number;
  isDesktop: boolean; onPageChange: (p: number) => void; onSizeChange: (s: number) => void;
}) {
  const from = Math.min(page * size + 1, totalElements);
  const to   = Math.min((page + 1) * size, totalElements);

  if (isDesktop) {
    const btnSt = (disabled: boolean): React.CSSProperties => ({
      fontSize: 12, fontWeight: 500, fontFamily: T.font,
      color: disabled ? T.t3 : T.t1,
      background: T.surface, border: `1px solid ${T.cardBorder}`,
      borderRadius: 6, padding: '6px 12px',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    });
    const pageBtnSt = (active: boolean): React.CSSProperties => ({
      fontSize: 12, fontWeight: active ? 600 : 400, fontFamily: T.font,
      color: active ? '#fff' : T.t1,
      background: active ? T.accent : T.surface,
      border: active ? `1px solid ${T.accent}` : `1px solid ${T.cardBorder}`,
      borderRadius: 6, padding: '6px 10px', cursor: 'pointer', minWidth: 32, textAlign: 'center' as const,
    });
    const cur = page + 1;
    const getPages = (): (number | '...')[] => {
      const pages: (number | '...')[] = [];
      if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
      else {
        pages.push(1);
        if (cur > 3) pages.push('...');
        for (let i = Math.max(2, cur - 1); i <= Math.min(totalPages - 1, cur + 1); i++) pages.push(i);
        if (cur < totalPages - 2) pages.push('...');
        pages.push(totalPages);
      }
      return pages;
    };
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center', padding: '10px 0 0', fontFamily: T.font }}>
        <div className="eod-summary-footer__size">
          <span>Rows per page:</span>
          <select value={size} onChange={e => { onSizeChange(Number(e.target.value)); onPageChange(0); }} className="eod-summary-footer__select">
            {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <span style={{ textAlign: 'center', fontSize: 12, color: T.t3 }}>
          Showing {from}–{to} of {totalElements}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
          <button onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0} style={btnSt(page === 0)}>Prev</button>
          {getPages().map((p, i) => p === '...'
            ? <span key={`e${i}`} style={{ padding: '0 4px', color: T.t3 }}>...</span>
            : <button key={p} onClick={() => onPageChange((p as number) - 1)} style={pageBtnSt(p === cur)}>{p}</button>
          )}
          <button onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={btnSt(page >= totalPages - 1)}>Next</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 2px 2px' }}>
      <span style={{ fontSize: 11, color: T.t3 }}>Showing {from}–{to} of {totalElements}</span>
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={page === 0} onClick={() => onPageChange(Math.max(0, page - 1))} className="eod-pagination__btn">Previous</button>
          <button type="button" disabled={page >= totalPages - 1} onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))} className="eod-pagination__btn">Next</button>
        </div>
      )}
    </div>
  );
}

function SummaryReportTable({ datePeriod }: { datePeriod: string }) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;

  const [salesType, setSalesType] = useState<'direct' | 'thirdParty'>('direct');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sortBy, setSortBy] = useState('date');
  const [sortAsc, setSortAsc] = useState(true);
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sectionError, setSectionError] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    setSectionError(false);
    getEodReports({
      locationId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: page + 1,
      size,
      sortBy,
      sortAsc,
      token,
    })
      .then((res: any) => {
        if (id !== fetchIdRef.current) return;
        setApiData(res);
        setSectionError(false);
      })
      .catch(() => {
        if (id !== fetchIdRef.current) return;
        setApiData(null);
        setSectionError(true);
      })
      .finally(() => {
        if (id === fetchIdRef.current) setLoading(false);
      });
  }, [locationId, dateRange, page, size, sortBy, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [dateRange.startDate, dateRange.endDate, size]);

  const content = apiData?.content ?? [];
  const totals = apiData?.totals ?? null;
  const totalPages = apiData?.totalPages ?? 1;
  const totalElements = apiData?.totalElements ?? 0;

  const headers = salesType === 'direct' ? SUMMARY_DIRECT_HEADERS : SUMMARY_THIRDPARTY_HEADERS;

  const handleSort = (key: string) => {
    if (sortBy === key) setSortAsc(prev => !prev);
    else { setSortBy(key); setSortAsc(true); }
    setPage(0);
  };

  const fmtCell = (key: string, value: any) => {
    if (key === 'date') return value === 'TOTALS' ? 'TOTALS' : fmtDateDisplay(value);
    return getCurrencySymbol() + fmt2(Number(value ?? 0));
  };

  /* ── Export: fetch all (optionally for another outlet), shape rows per format ── */
  const fetchAllForExport = useCallback((overrideLocationId?: string): Promise<any> => {
    return getEodReports({
      locationId: overrideLocationId ?? locationId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: 1,
      size: totalElements || 100,
      sortBy,
      sortAsc,
      token,
    });
  }, [locationId, dateRange, totalElements, sortBy, sortAsc, token]);

  const buildExportRows = (allContent: any[], allTotals: any, dateFn: (v: any) => string) =>
    [...allContent, ...(allTotals ? [allTotals] : [])].map((row: any) =>
      headers.map(h =>
        h.key === 'date'
          ? (row.date === 'TOTALS' ? 'TOTALS' : dateFn(row.date))
          : formatMoneyForExport(Number(row[h.key] ?? 0)),
      ),
    );

  const exportHeaders = headers.map(h => h.label);

  return (
    <ReportCard
      title="Summary Report"
      icon="summarize"
      flex={1}
      minHeight={0}
      action={
        <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
          title="Summary Report"
          filename="summary-report"
          buildExport={async (format, opts) => {
            const res = await fetchAllForExport(opts?.locationId);
            const rows = format === 'pdf'
              ? buildExportRows(res?.content ?? [], res?.totals ?? null, fmtDateDisplay)
              : buildExportRows(res?.content ?? [], res?.totals ?? null, fmtCsvDateCell);
            return { headers: exportHeaders, rows, totalRow: !!res?.totals };
          }}
        />
      }
    >
      <>
        {/* Radio toggle: Direct Sales / 3rd Party Sales */}
        <div className="eod-summary-radio-row">
          <label className={`eod-summary-radio ${salesType === 'direct' ? 'eod-summary-radio--active' : ''}`}>
            <input type="radio" name="summaryReportType" checked={salesType === 'direct'} onChange={() => { setSalesType('direct'); setPage(0); }} />
            <span className="eod-summary-radio__dot" />
            Direct Sales
          </label>
          <label className={`eod-summary-radio ${salesType === 'thirdParty' ? 'eod-summary-radio--active' : ''}`}>
            <input type="radio" name="summaryReportType" checked={salesType === 'thirdParty'} onChange={() => { setSalesType('thirdParty'); setPage(0); }} />
            <span className="eod-summary-radio__dot" />
            3rd Party Sales
          </label>
        </div>

        {loading ? (
          <EodTableShimmer cols={5} rows={6} />
        ) : sectionError ? (
          <SectionError kpiName="summary report" onRetry={fetchData} size="medium" minHeight={240} />
        ) : content.length === 0 ? (
          <SectionEmpty kpiName="summary report" size="medium" minHeight={220} />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="eod-table eod-summary-table">
                <thead>
                  <tr className="eod-table__row">
                    {headers.map((h, i) => (
                      <th
                        key={h.key}
                        className={`eod-summary-table__th ${i === 0 ? 'eod-summary-table__th--date' : 'eod-summary-table__th--num'}`}
                        onClick={h.sortKey ? () => handleSort(h.sortKey) : undefined}
                      >
                        <span className="eod-summary-table__th-inner">
                          {h.label}
                          {h.sortKey && sortBy === h.sortKey && (
                            <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {content.map((row: any, idx: number) => (
                    <tr key={row.date + '-' + idx} className="eod-table__row">
                      {headers.map((h, i) => (
                        <td
                          key={h.key}
                          className={`eod-summary-table__td ${i === 0 ? 'eod-summary-table__td--date' : 'eod-summary-table__td--num'}`}
                        >
                          {fmtCell(h.key, row[h.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {totals && (
                    <tr className="eod-table__row eod-table__row--totals">
                      {headers.map((h, i) => (
                        <td
                          key={h.key}
                          className={`eod-summary-table__td ${i === 0 ? 'eod-summary-table__td--date' : 'eod-summary-table__td--num'}`}
                        >
                          {fmtCell(h.key, totals[h.key])}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <EodTableFooter
              page={page} size={size} totalElements={totalElements} totalPages={totalPages}
              isDesktop={isDesktop} onPageChange={setPage} onSizeChange={(s) => { setSize(s); setPage(0); }}
            />
          </>
        )}
      </>
    </ReportCard>
  );
}


/* ─── Funds Table Section — self-fetching with backend pagination + totals ─── */
function FundsTableSection({ datePeriod }: { datePeriod: string }) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sortAsc, setSortAsc] = useState(true);
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sectionError, setSectionError] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    setSectionError(false);
    getEodReports({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, page: page + 1, size, sortBy: 'date', sortAsc, token })
      .then((res: any) => { if (id !== fetchIdRef.current) return; setApiData(res); setSectionError(false); })
      .catch(() => { if (id !== fetchIdRef.current) return; setApiData(null); setSectionError(true); })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  }, [locationId, dateRange, page, size, sortAsc, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [dateRange.startDate, dateRange.endDate, size]);

  const mapRow = (row: any) => {
    const expected = Number(row.cardSale ?? 0);
    const processed = Number(row.processed ?? row.processedFunds ?? row.maghilFunding ?? expected);
    return { date: row.date, expected, processed };
  };

  const rawContent: any[] = apiData?.content ?? [];
  const rawTotals: any = apiData?.totals ?? null;
  const content = rawContent.map(mapRow);
  const totals = rawTotals ? mapRow(rawTotals) : null;
  const totalPages: number = apiData?.totalPages ?? 1;
  const totalElements: number = apiData?.totalElements ?? 0;

  const fetchAllForExport = useCallback((overrideLocationId?: string): Promise<any> =>
    getEodReports({ locationId: overrideLocationId ?? locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, page: 1, size: totalElements || 100, sortBy: 'date', sortAsc, token }),
  [locationId, dateRange, totalElements, sortAsc, token]);

  const buildExportRows = (allContent: any[], allTotals: any, dateFn: (d: string) => string) =>
    [...allContent, ...(allTotals ? [allTotals] : [])].map((row: any) => {
      const r = row.date !== undefined && row.expected !== undefined ? row : mapRow(row);
      return [
        r.date === 'TOTALS' ? 'TOTALS' : dateFn(r.date ?? ''),
        formatMoneyForExport(Number(r.expected)),
        formatMoneyForExport(Number(r.processed)),
      ];
    });

  const renderRow = (row: { date: string; expected: number; processed: number }, key: string, isTotals = false) => {
    const pm = Math.abs(row.expected - row.processed) > 0.005;
    const dateLabel = isTotals ? 'TOTALS' : fmtDateDisplay(row.date);
    if (isDesktop) {
      return (
        <tr key={key} className={`eod-table__row${isTotals ? ' eod-table__row--totals' : ''}`}>
          <td className="eod-table__td--first">{dateLabel}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(row.expected)}</td>
          <td className={`eod-table__td--last ${pm ? 'eod-table__cell--highlight-orange' : 'eod-table__cell'}`}>{getCurrencySymbol()}{fmt2(row.processed)}</td>
        </tr>
      );
    }
    return (
      <div key={key} className={`eod-mobile-table__row${isTotals ? ' eod-mobile-table__row--totals' : ''}`}>
        <span className="eod-mobile-table__date eod-mobile-table__date--funds">{dateLabel}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(row.expected)}</span>
        <span className={`eod-mobile-table__cell eod-mobile-table__cell--last ${pm ? 'eod-table__cell--highlight-orange' : ''}`}>{getCurrencySymbol()}{fmt2(row.processed)}</span>
      </div>
    );
  };

  const tableBody = (
    <>
      {loading ? (
        <EodTableShimmer cols={3} rows={6} />
      ) : sectionError ? (
        <SectionError kpiName="funds report" onRetry={fetchData} size="medium" minHeight={240} />
      ) : content.length === 0 ? (
        <SectionEmpty kpiName="funds report" size="medium" minHeight={220} />
      ) : (
        <>
          {isDesktop ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="eod-table">
                <thead>
                  <tr className="eod-table__row">
                    <th className="eod-table__th eod-table__th--first eod-table__th--sortable" onClick={() => { setSortAsc(p => !p); setPage(0); }}>
                      Date <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>
                    </th>
                    <th className="eod-table__th eod-table__th--mid">Expected Funds</th>
                    <th className="eod-table__th eod-table__th--last">Processed Funds</th>
                  </tr>
                </thead>
                <tbody>
                  {content.map((row, i) => renderRow(row, row.date + '-' + i))}
                  {totals && renderRow(totals, 'totals-row', true)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="eod-mobile-table">
              <div className="eod-mobile-table__inner">
                <div className="eod-mobile-table__header">
                  <span className="eod-mobile-table__date--funds eod-table__th--sortable" onClick={() => { setSortAsc(p => !p); setPage(0); }}>
                    Date <span className="eod-table__sort-icon">{sortAsc ? '↑' : '↓'}</span>
                  </span>
                  <span className="eod-mobile-table__cell">Expected Funds</span>
                  <span className="eod-mobile-table__cell eod-mobile-table__cell--last">Processed Funds</span>
                </div>
                {content.map((row, i) => renderRow(row, row.date + '-' + i))}
                {totals && renderRow(totals, 'totals-row', true)}
              </div>
            </div>
          )}
          <EodTableFooter
            page={page} size={size} totalElements={totalElements} totalPages={totalPages}
            isDesktop={isDesktop} onPageChange={setPage} onSizeChange={(s) => { setSize(s); setPage(0); }}
          />
        </>
      )}
    </>
  );

  return (
    <ReportCard
      title="Funds"
      subtitle="Expected vs Processed"
      icon="account_balance"
      flex={1}
      minHeight={0}
      action={
        <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
          title="Funds Report"
          filename="funds-report"
          buildExport={async (format, opts) => {
            const res = await fetchAllForExport(opts?.locationId);
            const rows = format === 'pdf'
              ? buildExportRows(res?.content ?? [], res?.totals ?? null, fmtDateDisplay)
              : buildExportRows(res?.content ?? [], res?.totals ?? null, fmtCsvDateCell);
            return { headers: ['Date', 'Expected Funds', 'Processed Funds'], rows, totalRow: !!res?.totals };
          }}
        />
      }
    >
      {tableBody}
    </ReportCard>
  );
}

/* ─── Taxes Table Section — self-fetching with backend pagination + totals ─── */
function TaxesTableSection({ datePeriod, taxesRef, onTaxDrill }: any) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const { auth } = useAuth();

  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const token = useMemo(() => {
    if (!auth) return null;
    const search = (obj: any, depth = 0): string | null => {
      if (!obj || typeof obj !== 'object' || depth > 3) return null;
      for (const k of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[k] && typeof obj[k] === 'string' && obj[k].length > 10) return obj[k];
      }
      for (const [, v] of Object.entries(obj)) {
        if (typeof v === 'string' && (v as string).length > 50) return v as string;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const found = search(v, depth + 1);
          if (found) return found;
        }
      }
      return null;
    };
    return search(auth);
  }, [auth]);

  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sectionError, setSectionError] = useState(false);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!locationId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    setSectionError(false);
    getEodTaxesData({ locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, page: page + 1, size, token })
      .then((res: any) => { if (id !== fetchIdRef.current) return; setApiData(res); setSectionError(false); })
      .catch(() => { if (id !== fetchIdRef.current) return; setApiData(null); setSectionError(true); })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  }, [locationId, dateRange, page, size, token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [dateRange.startDate, dateRange.endDate, size]);

  const content: any[] = apiData?.content ?? [];
  const totals: any = apiData?.totals ?? null;
  const totalPages: number = apiData?.totalPages ?? 1;
  const totalElements: number = apiData?.totalElements ?? 0;

  const fetchAllForExport = useCallback((overrideLocationId?: string): Promise<any> =>
    getEodTaxesData({ locationId: overrideLocationId ?? locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, page: 1, size: totalElements || 100, token }),
  [locationId, dateRange, totalElements, token]);

  const buildExportRows = (allContent: any[], allTotals: any, dateFn: (v: any) => string) =>
    [...allContent, ...(allTotals ? [allTotals] : [])].map((row: any) => [
      row.date === 'TOTALS' ? 'TOTALS' : dateFn(row.date ?? ''),
      formatMoneyForExport(Number(row.taxableSales ?? 0)),
      formatMoneyForExport(Number(row.computedTax ?? 0)),
      formatMoneyForExport(Number(row.collectedTax ?? 0)),
      formatMoneyForExport(Number(row.difference ?? 0)),
    ]);

  const renderRow = (row: any, key: string, isTotals = false) => {
    const hd = Number(row.difference ?? 0) > 0.05;
    const cls = isTotals ? 'eod-table__row eod-table__row--totals' : 'eod-table__row';
    if (isDesktop) {
      return (
        <tr key={key} className={cls}>
          <td className="eod-table__td--first">{isTotals ? 'TOTALS' : fmtDateDisplay(row.date)}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(Number(row.taxableSales ?? 0))}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(Number(row.computedTax ?? 0))}</td>
          <td className="eod-table__td--mid eod-table__cell">{getCurrencySymbol()}{fmt2(Number(row.collectedTax ?? 0))}</td>
          <td className={`eod-table__td--last ${hd ? 'eod-table__cell--highlight-red' : 'eod-table__cell--highlight-green'}`}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {getCurrencySymbol()}{fmt2(Number(row.difference ?? 0))}
              {hd && !isTotals && onTaxDrill && (
                <span className="eod-tax-disc__drill-icon" title="View order details" onClick={() => onTaxDrill(row.date)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </span>
              )}
            </span>
          </td>
        </tr>
      );
    }
    return (
      <div key={key} className={`eod-mobile-table__row${isTotals ? ' eod-mobile-table__row--totals' : ''}`}>
        <span className="eod-mobile-table__date eod-mobile-table__date--taxes">{isTotals ? 'TOTALS' : fmtDateDisplay(row.date)}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(Number(row.taxableSales ?? 0))}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(Number(row.computedTax ?? 0))}</span>
        <span className="eod-mobile-table__cell">{getCurrencySymbol()}{fmt2(Number(row.collectedTax ?? 0))}</span>
        <span className={`eod-mobile-table__diff ${hd ? 'eod-table__cell--highlight-red' : 'eod-table__cell--highlight-green'}`}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {getCurrencySymbol()}{fmt2(Number(row.difference ?? 0))}
            {hd && !isTotals && onTaxDrill && (
              <span className="eod-tax-disc__drill-icon" title="View order details" onClick={() => onTaxDrill(row.date)}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </span>
            )}
          </span>
        </span>
      </div>
    );
  };

  const tableBody = (
    <>
      {loading ? (
        <EodTableShimmer cols={5} rows={6} />
      ) : sectionError ? (
        <SectionError kpiName="taxes report" onRetry={fetchData} size="medium" minHeight={240} />
      ) : content.length === 0 ? (
        <SectionEmpty kpiName="taxes report" size="medium" minHeight={220} />
      ) : (
        <>
          {isDesktop ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="eod-table eod-table--taxes">
                <thead>
                  <tr className="eod-table__row">
                    <th className="eod-table__th eod-table__th--first">Date</th>
                    <th className="eod-table__th eod-table__th--mid">Taxable Sales</th>
                    <th className="eod-table__th eod-table__th--mid">Computed Tax</th>
                    <th className="eod-table__th eod-table__th--mid">Collected Tax</th>
                    <th className="eod-table__th eod-table__th--last">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {content.map((row: any, i: number) => renderRow(row, row.date + '-' + i))}
                  {totals && renderRow(totals, 'totals-row', true)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="eod-mobile-table">
              <div className="eod-mobile-table__inner--wide">
                <div className="eod-mobile-table__header">
                  <span className="eod-mobile-table__date--taxes">Date</span>
                  <span className="eod-mobile-table__cell">Taxable Sales</span>
                  <span className="eod-mobile-table__cell">Computed Tax</span>
                  <span className="eod-mobile-table__cell">Collected Tax</span>
                  <span className="eod-mobile-table__diff">Difference</span>
                </div>
                {content.map((row: any, i: number) => renderRow(row, row.date + '-' + i))}
                {totals && renderRow(totals, 'totals-row', true)}
              </div>
            </div>
          )}
          <EodTableFooter
            page={page} size={size} totalElements={totalElements} totalPages={totalPages}
            isDesktop={isDesktop} onPageChange={setPage} onSizeChange={(s) => { setSize(s); setPage(0); }}
          />
        </>
      )}
    </>
  );

  return (
    <div ref={taxesRef}>
      <ReportCard
        title="Taxes"
        subtitle={isDesktop ? 'Computed vs Collected tax reconciliation' : 'Computed vs Collected'}
        icon="receipt_long"
        flex={1}
        minHeight={0}
        action={
          <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
            title="Taxes Report"
            filename="taxes-report"
            buildExport={async (format, opts) => {
              const res = await fetchAllForExport(opts?.locationId);
              const rows = format === 'pdf'
                ? buildExportRows(res?.content ?? [], res?.totals ?? null, fmtDateDisplay)
                : buildExportRows(res?.content ?? [], res?.totals ?? null, fmtCsvDateCell);
              return { headers: ['Date', 'Taxable Sales', 'Computed Tax', 'Collected Tax', 'Difference'], rows, totalRow: !!res?.totals };
            }}
          />
        }
      >
        {tableBody}
      </ReportCard>
    </div>
  );
}

/* ─── Sales-breakdown waterfall (ZAR-1462) ───
 * EOD has no channel toggle, so it renders the blended "overall" ladder. It pulls the
 * SAME ClickHouse sources that back the Snapshot/Sales stamp KPIs (grandTotal, void-summary,
 * snapshot-details discount, comp-order-log, refund-summary, tax, staff-performance tips,
 * gratuity) and dispatches them itself — always overall (no orderSource). */
function EodSalesWaterfall({ datePeriod }: { datePeriod: string }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // SW3 — EOD's waterfall has no adjacent KPI row of its own (unlike Snapshot/Sales' Net Sales
  // drill) — its own KPI cards are page-level, further up, unrelated to the waterfall. So the
  // return destination here is the waterfall section itself, not a KPI row.
  const waterfallRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchParams.get('scrollTo') !== 'waterfall') return;
    requestAnimationFrame(() => waterfallRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { selectedBranch: branch } = useBranch();
  const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const dateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);

  const snapshotDetails = useAppSelector((s) => s.clickHouse.snapshotDetailsSuccess) as any;
  const liveVoidSummary = useAppSelector((s) => s.clickHouse.liveVoidSummarySuccess) as { cancelledAmt: number }[];
  const liveRefundSummary = useAppSelector((s) => s.clickHouse.liveRefundSummarySuccess) as { cancelledAmt: number }[];
  const liveCompOrderLog = useAppSelector((s) => s.clickHouse.liveCompOrderLog) as { amount: number }[];
  const liveStaffPerformance = useAppSelector((s) => s.clickHouse.liveStaffPerformanceSuccess) as { tip: number }[];
  const loading = useAppSelector((s) => s.clickHouse.snapshotDetailsLoading);

  useEffect(() => {
    if (!locationId) return;
    const { startDate, endDate } = dateRange;
    dispatch(fetchSnapshotDetailsRequest(locationId, startDate, endDate));
    dispatch(fetchLiveVoidSummaryRequest(locationId, startDate, endDate));
    dispatch(fetchLiveRefundSummaryRequest(locationId, startDate, endDate));
    dispatch(fetchLiveStaffPerformanceRequest({ locationId, startDate, endDate }));
    dispatch(fetchLiveCompOrderLogRequest({ locationId, page: 1, size: 10, startDate, endDate }));
  }, [dispatch, locationId, dateRange.startDate, dateRange.endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = buildWaterfallData({
    voids: liveVoidSummary.reduce((s, i) => s + (Number(i.cancelledAmt) || 0), 0),
    discounts: Number(snapshotDetails?.discount) || 0,
    complimentary: liveCompOrderLog.reduce((s, o) => s + (Number(o.amount) || 0), 0),
    refunds: liveRefundSummary.reduce((s, i) => s + (Number(i.cancelledAmt) || 0), 0),
    netSales: Number(snapshotDetails?.netSales) || 0,
    tax: Number(snapshotDetails?.tax) || 0,
    tips: liveStaffPerformance.reduce((s, i) => s + (Number(i.tip) || 0), 0),
    gratuity: Number(snapshotDetails?.gratuity) || 0,
    otherCharges: Number(snapshotDetails?.otherCharges) || 0,
    totalCollected: Number(snapshotDetails?.grandTotal) || 0,
  });

  // SW3 — EOD has no Voids/Discounts/Complimentary/Refunds drill content of its own for the
  // waterfall (its "Total Refunds" stamp KPI drill is a separate entry point); all four route
  // to Snapshot's respective drilldown instead, consistent across desktop and mobile (this
  // component renders both — one unified responsive screen, no device fork).
  return (
    <div ref={waterfallRef}>
      <SalesWaterfall
        source="overall"
        data={data}
        fmt={fmtWaterfall}
        loading={loading}
        // SW3 — EOD's waterfall lives on the main page (not behind its own drillView), so
        // returning just needs to land back on the plain EOD route — the ?scrollTo=waterfall
        // param (read above) brings the waterfall itself into view on arrival.
        onDeductionClick={(key) => navigate(`/reports/snapshot?drill=${key}`, { state: { returnTo: '/reports/eod?scrollTo=waterfall' } })}
      />
    </div>
  );
}

/* ─── Shared responsive content (desktop + mobile) ─── */
function EODContent({ onDrill, onTaxDrill, taxesRef, data, datePeriod }: any) {
  const isDesktop = useIsDesktop();
  const { selectedBranch: branch } = useBranch();
  const eodLocationId = useMemo(() => branch?.id ?? branch?.locationId ?? '', [branch]);
  const sbcDateRange = useMemo(() => parseDateRange(datePeriod), [datePeriod]);
  const sbcIsToday = useMemo(
    () => String(datePeriod || '').trim().toLowerCase().replace(/\s+/g, '') === 'today',
    [datePeriod],
  );

  // Same resolution the other EOD section exports use, so this KPI's download
  // states the period in the same words as theirs.
  const sbcExportDateRangeLabel = exportPeriodFor(datePeriod, sbcDateRange)?.label ?? datePeriod;

  // Sales by Channel — Financial Breakdown (ZAR-1555)
  const salesByChannelBreakdown = useSalesByChannelBreakdown({
    locationId: eodLocationId,
    startDate: sbcDateRange.startDate,
    endDate: sbcDateRange.endDate,
    isToday: sbcIsToday,
  });

  const kpiItems = data?.kpiItems?.length ? data.kpiItems : buildKpiItemsDefault();
  const tenderDataActual = normalizeTenderPercentages(data?.tenderData ?? []);
  const channelDataActual = data?.channelData ?? [];

  return (
    <div className="eod-content">
      {/* KPI row */}
      <div className="eod-content__kpi-row">
        {kpiItems.map((item: any) => (
          <KPICard
            key={item.label}
            label={item.label}
            value={item.value}
            delta={item.delta}
            deltaLabel={item.deltaLabel}
            icon={item.icon}
            iconColor={item.iconColor}
            blurColor={item.blurColor}
            cardBg={item.cardBg}
            sub={item.sub}
            tooltip={item.tooltip}
            drillable={!!onDrill && !!item.drillKey}
            onTap={onDrill && item.drillKey ? () => onDrill(item.drillKey) : undefined}
          />
        ))}
      </div>

      <div className="eod-content__chart-row">
        <ReportCard
          title="Tender Mix"
          subtitle={isDesktop ? "Revenue by payment method" : undefined}
          icon="pie_chart"
          flex={0.7}
          minHeight={isDesktop ? 220 : undefined}
          drillable={!!onDrill}
          onTap={onDrill ? () => onDrill("tender") : undefined}
        >
          <TenderMixChart data={tenderDataActual} />
        </ReportCard>
        <ReportCard
          title="Channel Revenue"
          subtitle="Maghil vs Third-Party by channel"
          icon="device_hub"
          flex={1.3}
          minHeight={isDesktop ? 200 : undefined}
          drillable={!!onDrill}
          onTap={onDrill ? () => onDrill("channels") : undefined}
        >
          <ChannelRevenueChart data={channelDataActual} />
        </ReportCard>
      </div>

      {datePeriod && <SummaryReportTable datePeriod={datePeriod} />}

      {datePeriod && <FundsTableSection datePeriod={datePeriod} />}

      <TaxesTableSection datePeriod={datePeriod} taxesRef={taxesRef} onTaxDrill={onTaxDrill} />

      {/* Sales by Channel — Financial Breakdown (ZAR-1555, before the Sales waterfall) */}
      <SalesByChannelBreakdown
        data={salesByChannelBreakdown.groups}
        totals={salesByChannelBreakdown.totals}
        loading={salesByChannelBreakdown.loading}
        error={salesByChannelBreakdown.error}
        onRetry={salesByChannelBreakdown.retry}
        outletName={(branch as any)?.name}
        dateRange={{ startDate: sbcDateRange.startDate, endDate: sbcDateRange.endDate, isToday: sbcIsToday }}
        dateRangeLabel={sbcExportDateRangeLabel}
      />

      {/* ZAR-1462: existing "Sales to Funding" waterfall replaced by the shared
          sales-breakdown waterfall (EOD is always the blended "overall" source). */}
      <div className="eod-content__chart-row">
        {datePeriod && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <EodSalesWaterfall datePeriod={datePeriod} />
          </div>
        )}
      </div>
    </div>
  );
}

const DRILL_TITLES_DESKTOP: any = { tender: "Tender Types", tips: TG_DRILL_TITLE, refunds: "Refunds", channels: "Channels", taxDiscrepancy: "Tax Discrepancy Audit Log" };

function DesktopDrillContent({ view, data, onBack, dateRange, isToday }: any) {
  const exportDateRangeLabel = exportPeriodFor(isToday ? 'Today' : undefined, isToday ? null : dateRange)?.label ?? 'Today';
  const chData = data?.channelData ?? [];
  const tenderList = normalizeTenderPercentages(data?.tenderData ?? []);
  const cs = data?.channelSummary;
  const { auth } = useAuth();
  const authToken = useMemo(() => (auth ? findAuthToken(auth) : null), [auth]);

  if (view === "tender") {
    const hasBreakdown = tenderList.length && tenderList.some((r: any) => r.totalSales != null || r.orders != null);
    const totalCollected = hasBreakdown ? tenderList.reduce((s: number, r: any) => s + (Number(r.totalSales) || 0), 0) : 0;
    const topTenders = hasBreakdown ? tenderList.slice(0, 3) : [];
    const buildTenderExportRows = (rows: any[]) => rows.map((r: any) => {
      const orders = Number(r.orders) || 0;
      const sales = Number(r.totalSales) || 0;
      const avgTicket = orders > 0 ? sales / orders : 0;
      return [
        r.name,
        orders > 0 ? orders : '—',
        sales > 0 ? formatMoneyForExport(sales) : '—',
        avgTicket > 0 ? formatMoneyForExport(avgTicket) : '—',
        `${Number(r.value).toFixed(2)}%`,
      ];
    });
    return (
      <div className="eod-drill">
        <div className="eod-drill__kpi-row">
          <KPICard label="Total Collected" value={totalCollected > 0 ? fmt(totalCollected) : "—"} />
          {topTenders.map((t: any) => (
            <KPICard key={t.name} label={t.name} value={fmt(t.totalSales ?? 0)} />
          ))}
        </div>
        <ReportCard
          title="Tender Type Breakdown"
          subtitle="Detailed sales by payment method"
          icon="list_alt"
          action={hasBreakdown ? (
            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
              variant="desktop"
              title="Tender Type Breakdown"
              filename="eod-tender-type-breakdown"
              buildExport={async (_format, opts) => {
                const rows = opts?.locationId
                  ? await fetchTenderListForOutlet(opts.locationId, authToken, dateRange, !!isToday)
                  : tenderList;
                return {
                  headers: ['Tender type', 'Transactions', 'Total sales', 'Avg ticket', '% of total'],
                  rows: buildTenderExportRows(rows),
                };
              }}
            />
          ) : undefined}
        >
          {hasBreakdown ? (
            <div style={{ overflowX: "auto" }}>
              <table className="eod-tender-table">
                <thead>
                  <tr className="eod-tender-table__thead-row">
                    <th className="eod-tender-table__th eod-tender-table__th--first">Tender type</th>
                    <th className="eod-tender-table__th" style={{ textAlign: "right" }}>Transactions</th>
                    <th className="eod-tender-table__th" style={{ textAlign: "right" }}>Total sales</th>
                    <th className="eod-tender-table__th" style={{ textAlign: "right" }}>Avg ticket</th>
                    <th className="eod-tender-table__th eod-tender-table__th--last" style={{ textAlign: "right" }}>% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {tenderList.map((r: any) => {
                    const orders = Number(r.orders) || 0;
                    const sales = Number(r.totalSales) || 0;
                    const avgTicket = orders > 0 ? sales / orders : 0;
                    return (
                      <tr key={r.name} className="eod-tender-table__row">
                        <td className="eod-tender-table__name">{r.name}</td>
                        <td className="eod-tender-table__cell">{orders > 0 ? orders.toLocaleString() : "—"}</td>
                        <td className="eod-tender-table__cell eod-tender-table__cell--bold">{sales > 0 ? fmt(sales) : "—"}</td>
                        <td className="eod-tender-table__cell eod-tender-table__cell--muted">{avgTicket > 0 ? getCurrencySymbol() + avgTicket.toFixed(2) : "—"}</td>
                        <td className="eod-tender-table__cell eod-tender-table__cell--accent eod-tender-table__cell--last">{Number(r.value).toFixed(2)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : tenderList.length > 0 ? (
            tenderList.map((r: any, i: number, arr: any[]) => (
              <div key={r.name} className={`eod-tender-list__item ${i < arr.length - 1 ? "eod-tender-list__item--bordered" : ""}`}>
                <Icon name="credit_card" size={16} color={T.accent} style={{ marginRight: 8 }} />
                <div style={{ flex: 1 }}>
                  <div className="eod-tender-list__name">{r.name}</div>
                </div>
                <div className="eod-tender-list__value">
                  <div className="eod-tender-list__pct">{r.value != null ? `${Number(r.value).toFixed(2)}%` : "—"}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="eod-no-data">No tender data for this period.</div>
          )}
        </ReportCard>
      </div>
    );
  }
  // ZAR-1762 / TG-2 - "Tips & Gratuity" drill-in, direct-only (EOD has no source toggle
  // and no 3rd-party gratuity context). Section totals come from the EOD summary, which is
  // the reconciliation-grade figure; the staff rankings come from staffTipAndGratuity.
  if (view === "tips") {
    return (
      <div className="eod-drill">
        <TipsGratuityDrill data={buildEodTipsGratuityData(data)} fmt={fmtTgMoney} />
      </div>
    );
  }
  if (view === "refunds") {
    const rs = data?.refundsSummary;
    const reasons = data?.refundReasons ?? [];
    const refundVal = rs?.refundAmount != null ? fmt(rs.refundAmount) : "—";
    const refundRateVal = rs?.refundRate != null ? `${rs.refundRate.toFixed(1)}%` : "—";
    return (
      <div className="eod-drill">
        <div className="eod-drill__kpi-row">
          <KPICard label="Refunds" value={refundVal} />
          <KPICard label="Refund Rate" value={refundRateVal} />
        </div>
        <ReportCard title="Refund Reasons" icon="help_outline" style={{ marginBottom: 16 }}>
          {reasons.length ? reasons.map((r: any) => (
            <div key={r.reason} className="eod-refund-reason">
              <div className="eod-refund-reason__header">
                <span className="eod-refund-reason__name">{r.reason}</span>
                <span className="eod-refund-reason__value">
                  {r.amount != null ? `${fmt(Number(r.amount) || 0)} (${r.pct}%)` : `${r.pct}%`}
                </span>
              </div>
              <div className="eod-refund-reason__bar">
                <div className="eod-refund-reason__bar-fill" style={{ width: `${r.pct}%`, background: r.color || T.accent }} />
              </div>
            </div>
          )) : (
            <SectionEmpty kpiName="refund reasons" />
          )}
        </ReportCard>
      </div>
    );
  }
  if (view === "channels") {
    const totalSalesVal = cs?.totalSales != null ? fmt(cs.totalSales) : (cs?.totalMaghil != null || cs?.totalThirdParty != null ? fmt((cs?.totalMaghil ?? 0) + (cs?.totalThirdParty ?? 0)) : "—");
    const maghilVal = cs?.totalMaghil != null ? fmt(cs.totalMaghil) : "—";
    const thirdPartyVal = cs?.totalThirdParty != null ? fmt(cs.totalThirdParty) : "—";
    const maghilPctVal = cs?.maghilPct != null ? `${cs.maghilPct.toFixed(1)}%` : "—";
    return (
      <div className="eod-drill">
        <div className="eod-drill__kpi-row">
          <KPICard label="Total Sales" value={totalSalesVal} />
          <KPICard label="Maghil" value={maghilVal} />
          <KPICard label="Third Party" value={thirdPartyVal} />
          <KPICard label="Maghil %" value={maghilPctVal} />
        </div>
        <ReportCard title="Revenue by Channel" icon="bar_chart" style={{ maxWidth: 560 }}>
          {chData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chData} margin={{ top: 5, right: 0, left: -20, bottom: 28 }}>
                <XAxis
                  dataKey="channel"
                  interval={0}
                  tick={{ fontSize: 10, fill: T.t3 }}
                  tickMargin={8}
                  angle={-35}
                  textAnchor="end"
                  height={44}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v, name) => [fmt(v), name]} />
                <Bar dataKey="maghil" name="Maghil" fill={T.accent} radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="thirdParty" name="Third Party" fill={T.orange} radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Legend verticalAlign="bottom" align="center" iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12, color: T.t2 }} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="eod-no-data">No channel data for this period.</div>
          )}
        </ReportCard>
      </div>
    );
  }
  return null;
}

function EODDesktopContent({ datePeriod, setDatePeriod, data, loading, error, locationId, loadStaffTipsPage, dateRange, isToday }: any) {
  const [drillView, setDrillView] = useState<any>(null);
  const [taxDrillDate, setTaxDrillDate] = useState<string | null>(null);
  const taxesAnchorRef = useRef<HTMLDivElement>(null);
  const setTopBarProps = useDesktopTopBar();

  const handleTaxDrillBack = useCallback(() => {
    setTaxDrillDate(null);
    setTimeout(() => {
      if (taxesAnchorRef.current) {
        taxesAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  }, []);

  useEffect(() => {
    const isAnyDrill = drillView || taxDrillDate;
    setTopBarProps({
      breadcrumbs: (drillView || taxDrillDate) ? "Reports › EOD Summary" : "Reports",
      title: taxDrillDate
        ? DRILL_TITLES_DESKTOP.taxDiscrepancy
        : drillView ? DRILL_TITLES_DESKTOP[drillView] ?? "EOD Summary" : "EOD Summary",
      dateFilters: isAnyDrill ? undefined : { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_EOD },
      leftAddon: drillView ? (
        <button
          type="button"
          onClick={() => setDrillView(null)}
          aria-label="Back to EOD Summary"
          className="eod-drill__back-btn"
        >
          <span className="material-symbols-rounded eod-drill__back-icon">arrow_back</span>
        </button>
      ) : undefined,
    });
    return () => setTopBarProps(null);
  }, [setTopBarProps, datePeriod, setDatePeriod, drillView, taxDrillDate]);

  if (!locationId) {
    return (
      <div className="eod-state-message eod-state-message--muted">
        Select a location to view EOD Summary.
      </div>
    );
  }
  if (loading) {
    return <EODDesktopShimmer />;
  }
  if (error) {
    return (
      <div className="eod-state-message eod-state-message--error">
        {error}
      </div>
    );
  }

  if (taxDrillDate) {
    return (
      <div className="eod-desktop-drill-wrapper">
        <TaxDiscrepancyDrillContent date={taxDrillDate} onBack={handleTaxDrillBack} />
      </div>
    );
  }

  if (drillView) {
    return (
      <div className="eod-desktop-drill-wrapper">
        <DesktopDrillContent view={drillView} data={data} onBack={() => setDrillView(null)} dateRange={dateRange} isToday={isToday} />
      </div>
    );
  }

  return (
    <div className="eod-desktop-content-wrapper">
      <EODContent data={data} onDrill={setDrillView} onTaxDrill={setTaxDrillDate} taxesRef={taxesAnchorRef} datePeriod={datePeriod} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE LAYOUT (TopBar + content + shared bottom nav)
   ═══════════════════════════════════════════════════════════════════════════ */

function MOverviewContent({ onDrill, onTaxDrill, taxesRef, data, datePeriod }: any) {
  return (
    <div className="eod-mobile-overview">
      <EODContent onDrill={onDrill} onTaxDrill={onTaxDrill} taxesRef={taxesRef} data={data} datePeriod={datePeriod} />
    </div>
  );
}

function MTenderDrill({ tenderData: tenderListRaw, dateRange, isToday }: any) {
  const exportDateRangeLabel = exportPeriodFor(isToday ? 'Today' : undefined, isToday ? null : dateRange)?.label ?? 'Today';
  const list = normalizeTenderPercentages(tenderListRaw ?? []);
  const hasBreakdown = list.length && list.some((r: any) => r.totalSales != null || r.orders != null);
  const totalCollected = hasBreakdown ? list.reduce((s: number, r: any) => s + (Number(r.totalSales) || 0), 0) : 0;
  const { auth } = useAuth();
  const authToken = useMemo(() => (auth ? findAuthToken(auth) : null), [auth]);
  const buildTenderExportRows = (rows: any[]) => rows.map((r: any) => {
    const orders = Number(r.orders) || 0;
    const sales = Number(r.totalSales) || 0;
    const avgTicket = orders > 0 ? sales / orders : 0;
    return [
      r.name,
      orders > 0 ? orders : '—',
      sales > 0 ? formatMoneyForExport(sales) : '—',
      avgTicket > 0 ? formatMoneyForExport(avgTicket) : '—',
      `${Number(r.value).toFixed(2)}%`,
    ];
  });
  return (
    <div className="eod-mobile-drill">
      <div className="eod-mobile-drill__kpi-row">
        <KPICard label="Total Collected" value={totalCollected > 0 ? fmt(totalCollected) : "—"} />
        {hasBreakdown && list.slice(0, 3).map((t: any) => (
          <KPICard key={t.name} label={t.name} value={fmt(t.totalSales ?? 0)} />
        ))}
      </div>
      <ReportCard
        title="Tender Type Breakdown"
        subtitle="Detailed sales by payment method"
        icon="list_alt"
        style={{ marginBottom: 8 }}
        action={hasBreakdown ? (
          <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
            variant="mobile"
            title="Tender Type Breakdown"
            filename="eod-tender-type-breakdown"
            buildExport={async (_format, opts) => {
              const rows = opts?.locationId
                ? await fetchTenderListForOutlet(opts.locationId, authToken, dateRange, !!isToday)
                : list;
              return {
                headers: ['Tender type', 'Transactions', 'Total sales', 'Avg ticket', '% of total'],
                rows: buildTenderExportRows(rows),
              };
            }}
          />
        ) : undefined}
      >
        {hasBreakdown ? (
          list.map((r: any) => {
            const orders = Number(r.orders) || 0;
            const sales = Number(r.totalSales) || 0;
            const avgTicket = orders > 0 ? sales / orders : 0;
            return (
              <div key={r.name} className="eod-mobile-tender__item">
                <div className="eod-mobile-tender__item-name">{r.name}</div>
                <div className="eod-mobile-tender__item-row">
                  <span>{orders > 0 ? orders.toLocaleString() + " orders" : "—"}</span>
                  <span>{sales > 0 ? fmt(sales) : "—"}</span>
                </div>
                <div className="eod-mobile-tender__item-bottom">
                  <span>Avg: {avgTicket > 0 ? getCurrencySymbol() + avgTicket.toFixed(2) : "—"}</span>
                  <span className="eod-mobile-tender__pct">{Number(r.value).toFixed(2)}%</span>
                </div>
              </div>
            );
          })
        ) : list.length > 0 ? (
          list.map((r: any, i: number) => (
            <div key={r.name} className={`eod-mobile-tender-list__item ${i < list.length - 1 ? "eod-mobile-tender-list__item--bordered" : ""}`}>
              <Icon name="credit_card" size={16} color={T.accent} style={{ marginRight: 8 }} />
              <div style={{ flex: 1 }}>
                <div className="eod-mobile-tender-list__name">{r.name}</div>
                <div className="eod-mobile-tender-list__orders">{r.orders != null ? r.orders + " orders" : "—"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="eod-mobile-tender-list__sales">{r.totalSales != null ? fmt(r.totalSales) : "—"}</div>
                <div className="eod-mobile-tender-list__pct">{r.value != null ? `${Number(r.value).toFixed(2)}%` : "—"}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="eod-no-data eod-no-data--sm">No tender data for this period.</div>
        )}
      </ReportCard>
    </div>
  );
}

/**
 * ZAR-1762 / TG-2 - mobile "Tips & Gratuity" drill-in for EOD Reconciliation.
 * Same shared two-section component as desktop; no source toggle (EOD is direct-only).
 */
function MTipsDrill({ data }: any) {
  return (
    <div className="eod-mobile-drill">
      <TipsGratuityDrill data={buildEodTipsGratuityData(data)} fmt={fmtTgMoney} isMobile />
    </div>
  );
}

function MRefundsDrill({ refundsSummary: rs, refundReasons: reasonsProp }: any) {
  const reasons = reasonsProp ?? [];
  const refundVal = rs?.refundAmount != null ? fmt(rs.refundAmount) : "—";
  const refundRateVal = rs?.refundRate != null ? `${rs.refundRate.toFixed(1)}%` : "—";
  return (
    <div className="eod-mobile-drill">
      <div className="eod-mobile-drill__kpi-row">
        <KPICard label="Refunds" value={refundVal} />
        <KPICard label="Refund Rate" value={refundRateVal} />
      </div>
      <ReportCard title="Refund Reasons" icon="help_outline" style={{ marginBottom: 8 }}>
        {reasons.length ? reasons.map((r: any) => (
          <div key={r.reason} className="eod-refund-reason">
            <div className="eod-refund-reason__header">
              <span className="eod-mobile-refund-reason__name" style={{ color: T.t2 }}>{r.reason}</span>
              <span className="eod-mobile-refund-reason__value" style={{ color: T.t1 }}>
                {r.amount != null ? `${fmt(Number(r.amount) || 0)} (${r.pct}%)` : `${r.pct}%`}
              </span>
            </div>
            <div className="eod-refund-reason__bar eod-mobile-refund-reason__bar">
              <div className="eod-refund-reason__bar-fill" style={{ width: `${r.pct}%`, background: r.color || T.accent }} />
            </div>
          </div>
        )) : (
          <SectionEmpty kpiName="refund reasons" />
        )}
      </ReportCard>
    </div>
  );
}

function MChannelsDrill({ channelData: channelDataProp, channelSummary: cs }: any) {
  const chData = channelDataProp ?? [];
  const totalSalesVal = cs?.totalSales != null ? fmt(cs.totalSales) : (cs?.totalMaghil != null || cs?.totalThirdParty != null ? fmt((cs?.totalMaghil ?? 0) + (cs?.totalThirdParty ?? 0)) : "—");
  const maghilVal = cs?.totalMaghil != null ? fmt(cs.totalMaghil) : "—";
  const thirdPartyVal = cs?.totalThirdParty != null ? fmt(cs.totalThirdParty) : "—";
  const maghilPctVal = cs?.maghilPct != null ? `${cs.maghilPct.toFixed(1)}%` : "—";
  return (
    <div className="eod-mobile-drill">
      <div className="eod-mobile-drill__kpi-row">
        <KPICard label="Total Sales" value={totalSalesVal} />
        <KPICard label="Maghil" value={maghilVal} />
        <KPICard label="Third Party" value={thirdPartyVal} />
        <KPICard label="Maghil %" value={maghilPctVal} />
      </div>
      <ReportCard title="Revenue by Channel" icon="bar_chart" style={{ marginBottom: 8 }}>
        {chData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chData} margin={{ top: 5, right: 0, left: -20, bottom: 24 }}>
              <XAxis
                dataKey="channel"
                interval={0}
                tick={{ fontSize: 8, fill: T.t3 }}
                tickMargin={6}
                angle={-35}
                textAnchor="end"
                height={40}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 7, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, name) => [fmt(v), name]} />
              <Bar dataKey="maghil" name="Maghil" stackId="a" fill={T.accent} maxBarSize={18} />
              <Bar dataKey="thirdParty" name="Third Party" stackId="a" fill={T.orange} radius={[3, 3, 0, 0]} maxBarSize={18} />
              <Legend verticalAlign="bottom" align="center" iconType="square" iconSize={9} wrapperStyle={{ fontSize: 10, color: T.t2 }} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="eod-no-data eod-no-data--sm">No channel data for this period.</div>
        )}
      </ReportCard>
    </div>
  );
}

const DRILL_TITLES: any = { overview: "EOD Summary", tender: "Tender Types", tips: TG_DRILL_TITLE, refunds: "Refunds", channels: "Channels", taxDiscrepancy: "Tax Discrepancy Audit Log" };

function MobileLayout({ datePeriod, setDatePeriod, data, loading, error, locationId, loadStaffTipsPage, refetch, dateRange, isToday }: any) {
  const setTopBarProps = useDesktopTopBar();
  const [screen, setScreen] = useState("overview");
  const [taxDrillDate, setTaxDrillDate] = useState<string | null>(null);
  const taxesAnchorRef = useRef<HTMLDivElement>(null);
  const isDrill = screen !== "overview";
  const handlePtrRefresh = useCallback(() => {
    refetch?.();
  }, [refetch]);
  const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

  useDrillHistoryBack(isDrill, () => { setScreen("overview"); setTaxDrillDate(null); });

  const handleTaxDrill = useCallback((date: string) => {
    setTaxDrillDate(date);
    setScreen('taxDiscrepancy');
  }, []);

  const handleTaxDrillMobileBack = useCallback(() => {
    setScreen('overview');
    setTaxDrillDate(null);
    setTimeout(() => {
      if (taxesAnchorRef.current) {
        taxesAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  }, []);

  useSwipeBack();

  useEffect(() => {
    setTopBarProps({
      title: isDrill ? DRILL_TITLES[screen] ?? "EOD Summary" : "EOD Summary",
      dateFilters: isDrill ? undefined : { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_EOD },
      onBack: isDrill ? (screen === 'taxDiscrepancy' ? handleTaxDrillMobileBack : () => setScreen("overview")) : undefined,
    });
  }, [setTopBarProps, screen, isDrill, datePeriod, setDatePeriod, handleTaxDrillMobileBack]);

  const drillContent: any = {
    tender: <MTenderDrill tenderData={data?.tenderData} dateRange={dateRange} isToday={isToday} />,
    tips: <MTipsDrill data={data} />,
    refunds: <MRefundsDrill refundsSummary={data?.refundsSummary} refundReasons={data?.refundReasons} voidLog={data?.voidLog} />,
    channels: <MChannelsDrill channelData={data?.channelData} channelSummary={data?.channelSummary} />,
    taxDiscrepancy: taxDrillDate ? <TaxDiscrepancyDrillContent date={taxDrillDate} onBack={handleTaxDrillMobileBack} /> : null,
  };

  const showContent = locationId && !loading && !error;
  return (
    <div className="eod-mobile-root">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
      <TopBar variant="mobile" />
      <div ref={ptrScrollRef} style={{ flex: 1, overflowY: 'auto' }}>
        {!locationId && (
          <div className="eod-state-message eod-state-message--muted eod-state-message--sm">Select a location to view EOD Summary.</div>
        )}
        {locationId && loading && <EODMobileShimmer />}
        {locationId && !loading && error && (
          <div className="eod-state-message eod-state-message--error eod-state-message--sm">{error}</div>
        )}
        {showContent && (screen === "overview"
          ? <MOverviewContent onDrill={setScreen} onTaxDrill={handleTaxDrill} taxesRef={taxesAnchorRef} data={data} datePeriod={datePeriod} />
          : drillContent[screen] || <MOverviewContent onDrill={setScreen} onTaxDrill={handleTaxDrill} taxesRef={taxesAnchorRef} data={data} datePeriod={datePeriod} />)}
      </div>
      <BottomNav />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ENTRY POINT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function EODSummaryReport() {
  const isDesktop = useIsDesktop();
  const [datePeriod, setDatePeriod] = useSharedReportDateFilter("Yesterday", { options: DATE_PRESETS_EOD });
  const { data, loading, error, locationId, loadStaffTipsPage, refetch, dateRange, isToday } = useEodSummaryData(datePeriod);

  if (isDesktop) {
    return (
      <DesktopLayout>
        <EODDesktopContent
          datePeriod={datePeriod}
          setDatePeriod={setDatePeriod}
          data={data}
          loading={loading}
          error={error}
          locationId={locationId}
          loadStaffTipsPage={loadStaffTipsPage}
          dateRange={dateRange}
          isToday={isToday}
        />
      </DesktopLayout>
    );
  }
  return (
    <MobileLayout
      datePeriod={datePeriod}
      setDatePeriod={setDatePeriod}
      data={data}
      loading={loading}
      error={error}
      locationId={locationId}
      loadStaffTipsPage={loadStaffTipsPage}
      refetch={refetch}
      dateRange={dateRange}
      isToday={isToday}
    />
  );
}
